import React from 'react';
import { Box, Grid, IconButton, Tooltip } from '@mui/material';
import { FieldArray, useFormikContext } from 'formik';
import { Plus, Trash2, UserRound } from 'lucide-react';
import Cookies from 'js-cookie';
import { DeletePartnerByID } from 'api/profile';
import { useToast } from 'context/ToastContext';
import { Secondary } from 'ui-components/Buttons';
import CustomValidationText from 'ui-components/CustomValidationText';
import ImageDropzone from '../components/ImageDropzone';
import { BORDER, FieldLabel, INDIGO, MUTED, SectionTitle, TextInput } from '../primitives';
import { capitalizeFirst } from './BasicDetails';

const EMPTY_PARTNER = { name: '', title: '', linkedin_url: '', bio: '', image: null };

// A partner only reaches the backend once it has a name, a LinkedIn URL and a
// photo — the same filter the old wizard's submit applies.
export const isPartnerComplete = (partner) =>
  String(partner?.name || '').trim().length > 0 &&
  String(partner?.linkedin_url || '').trim().length > 0 &&
  (typeof partner?.image !== 'string'
    ? partner?.image != null
    : String(partner?.image).length > 0);

const missingFields = (partner) => ({
  ...(String(partner?.name || '').trim() ? {} : { name: 'Please enter partner name' }),
  ...(String(partner?.linkedin_url || '').trim()
    ? {}
    : { linkedin_url: 'Please enter linkedin URL' }),
  ...((typeof partner?.image !== 'string' ? partner?.image != null : String(partner?.image).length > 0)
    ? {}
    : { image: 'Please add a photo' }),
});

const PartnerInformation = () => {
  const formik = useFormikContext();
  const { values, setFieldValue } = formik;
  const { addToast } = useToast();
  const { mutateAsync: deletePartner } = DeletePartnerByID();
  const investor_id = Cookies.get('X-INV-ID') || null;

  // Partners stay optional, so these live outside Yup: they only fire when the user
  // asks for another row while the current one is incomplete.
  const [rowErrors, setRowErrors] = React.useState({});

  const partners = values.partners?.length ? values.partners : [EMPTY_PARTNER];

  const clearError = (index, field) =>
    setRowErrors((previous) => {
      if (!previous[index]?.[field]) return previous;
      const { [field]: _removed, ...rest } = previous[index];
      return { ...previous, [index]: rest };
    });

  const setField = (index, field, value) => {
    setFieldValue(`partners.${index}.${field}`, value);
    clearError(index, field);
  };

  // An already-saved partner has an _id and must be deleted server-side too.
  const handleDelete = (partner, arrayHelpers, index) => {
    if (!window.confirm('Are you sure you want to delete?')) return;

    // Removing the last row would leave an empty array and the form would vanish —
    // reset it to a blank partner instead.
    const removeRow = () => {
      if (partners.length === 1) setFieldValue('partners', [{ ...EMPTY_PARTNER }]);
      else arrayHelpers.remove(index);
      setRowErrors({});
    };

    if (partner?._id) {
      deletePartner(
        { payload: { partner_id: partner._id, investor_id } },
        {
          onSuccess: () => {
            removeRow();
            addToast('Partner deleted!', 'success');
          },
          onError: (error) => addToast(error?.response?.data?.message, 'error'),
        }
      );
    } else {
      removeRow();
    }
  };

  const stripHttps = (v) =>
    String(v ?? '').startsWith('https://') ? String(v).replace('https://', '') : v ?? '';

  return (
    <FieldArray
      name="partners"
      render={(arrayHelpers) => {
        const lastIndex = partners.length - 1;

        const handleAddAnother = () => {
          const missing = missingFields(partners[lastIndex]);
          if (Object.keys(missing).length > 0) {
            setRowErrors((previous) => ({ ...previous, [lastIndex]: missing }));
            return;
          }
          arrayHelpers.push({ ...EMPTY_PARTNER });
        };

        return (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                mb: 3,
              }}
            >
              <Box>
                <SectionTitle sx={{ mb: 0.5 }}>Partners</SectionTitle>
                <Box sx={{ color: MUTED, fontSize: '0.9rem' }}>Tell us about your partners.</Box>
              </Box>
              <Secondary
                onClick={handleAddAnother}
                startIcon={<Plus size={18} />}
                // Figma: purple label + icon (the component supplies the gradient border).
                sx={{
                  py: '10px',
                  px: '16px',
                  whiteSpace: 'nowrap',
                  color: INDIGO,
                  fontWeight: 600,
                  '& .MuiButton-startIcon': { color: INDIGO, marginRight: '8px' },
                }}
              >
                Add Another Partner
              </Secondary>
            </Box>

            {partners.map((partner, index) => {
              const errors = rowErrors[index] || {};
              return (
                <Box
                  key={partner?._id || index}
                  sx={{
                    position: 'relative',
                    border: `1px solid ${BORDER}`,
                    borderRadius: '12px',
                    p: { xs: 2, sm: 2.5 },
                    mb: 2.5,
                  }}
                >
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm="auto">
                      <ImageDropzone
                        name={`partners.${index}.image`}
                        value={partner?.image}
                        title="Crop Photo"
                        // Figma: a 120x120 square, not a box stretched to the row height.
                        width={120}
                        height={120}
                        minHeight={120}
                        showHint={false}
                        showActions={false}
                        dashed={false}
                        icon={UserRound}
                        emptyIconSize={56}
                        error={errors.image}
                        handleUpdateFile={(file) => {
                          setFieldValue(`partners.${index}.image`, file);
                          clearError(index, 'image');
                        }}
                      />
                      {errors.image && <CustomValidationText value={errors.image} />}
                    </Grid>

                    <Grid item xs={12} sm>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <FieldLabel>Name</FieldLabel>
                          <TextInput
                            icon={UserRound}
                            name={`partners.${index}.name`}
                            placeholder="Enter partner name"
                            value={partner?.name}
                            error={errors.name}
                            onChange={(e) => setField(index, 'name', capitalizeFirst(e.target.value))}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FieldLabel>Title</FieldLabel>
                          <TextInput
                            name={`partners.${index}.title`}
                            placeholder="Enter partner title"
                            value={partner?.title}
                            onChange={(e) => setField(index, 'title', capitalizeFirst(e.target.value))}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FieldLabel>LinkedIn URL</FieldLabel>
                          <TextInput
                            prefix="https://"
                            name={`partners.${index}.linkedin_url`}
                            placeholder="Enter linkedin URL"
                            value={stripHttps(partner?.linkedin_url)}
                            error={errors.linkedin_url}
                            onChange={(e) =>
                              setField(index, 'linkedin_url', stripHttps(e.target.value))
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FieldLabel>Bio</FieldLabel>
                          <TextInput
                            name={`partners.${index}.bio`}
                            placeholder="Enter partner bio"
                            value={partner?.bio}
                            onChange={(e) => setField(index, 'bio', e.target.value)}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Tooltip title="Delete partner">
                    <IconButton
                      aria-label="Delete partner"
                      onClick={() => handleDelete(partner, arrayHelpers, index)}
                      sx={{ position: 'absolute', top: 8, right: 8, color: MUTED }}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </>
        );
      }}
    />
  );
};

export default PartnerInformation;
